import { isValidElement, type JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineRight } from 'react-icons/ai';

import {
  type AccordionProps,
  Box,
  Accordion as MuiAccordion,
  type SxProps,
} from '@mui/material';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import type { Theme } from '@mui/material/styles';

import isFunction from 'lodash/isFunction';

import { accordionSummaryWrapper } from '@components/common/Accordion/styles/sxProperties';

interface IAccordion {
  id: string;
  isOpened?: boolean;
  isDisabled?: boolean;
  title: string | JSX.Element;
  description?: string | JSX.Element;
  content?: null | JSX.Element | ((expanded: boolean) => null | JSX.Element);
  sxProperties?: {
    title?: SxProps<Theme>;
    description?: SxProps<Theme>;
  };
}

export const Accordion = ({
  id,
  title,
  description,
  content = null,
  isOpened = false,
  sxProperties = {},
  isDisabled = false,
}: IAccordion) => {
  const [isExpanded, setIsExpanded] = useState(isOpened);
  const { t } = useTranslation();

  const onHandleChange: AccordionProps['onChange'] = (_, isExpanded) => {
    setIsExpanded(isExpanded);
  };

  return (
    <MuiAccordion
      disabled={isDisabled}
      expanded={isExpanded}
      onChange={onHandleChange}
    >
      <AccordionSummary
        aria-controls="content"
        expandIcon={<AiOutlineRight />}
        id={id}
        sx={{ ...sxProperties.title }}
      >
        <Box sx={accordionSummaryWrapper}>
          {isValidElement(title) ? title : t(title)}
        </Box>
        {typeof description === 'string' ? (
          <Box sx={sxProperties.description}>{description}</Box>
        ) : (
          description
        )}
      </AccordionSummary>
      <AccordionDetails>
        {isFunction(content) ? content(isExpanded) : content}
      </AccordionDetails>
    </MuiAccordion>
  );
};
